import type { VoltStatusMap } from "../ui/voltStatus";
import type { Committee, Draft, Source } from "./types";

/** Title of a draft; until a run delivers one, the first line of the topic (as kurztitel on the server). */
export const draftTitle = (
  d: Pick<Draft, "titel" | "thema">,
  max = 120,
): string => {
  if (d.titel) return d.titel;
  const line =
    d.thema
      .split("\n")
      .map((l) =>
        l
          .replace(/^#+\s*/, "")
          .replace(/\*\*/g, "")
          .trim(),
      )
      .find((l) => l.length > 0) ?? "";
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
};

/** Makes committee spellings comparable ("Horn / Hamm" vs. "Horn/Hamm"). */
export const normalizeCommittee = (name: string) =>
  name.toLowerCase().replace(/[^a-zäöüß]/g, "");

/** Responsible person of a committee: the buddy, otherwise the members. */
export const buddyFor = (
  committees: Committee[],
  committee: string | null,
): string => {
  if (!committee) return "—";
  const target = normalizeCommittee(committee);
  const hit =
    committees.find((c) => normalizeCommittee(c.gremium) === target) ??
    committees.find(
      (c) =>
        target.includes(normalizeCommittee(c.gremium)) ||
        normalizeCommittee(c.gremium).includes(target),
    );
  if (!hit) return "—";
  const names = hit.buddy.length > 0 ? hit.buddy : hit.mitglied;
  return names.length > 0 ? names.join(", ") : "—";
};

export const isRunning = (d: Pick<Draft, "status">) =>
  d.status === "wartet" || d.status === "laeuft";

export const formatDateTime = (t: string) =>
  new Date(t).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const formatDate = (t: string) =>
  new Date(t).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const draftPath = (id: string) => `/workshop/draft/${id}`;

export const DRAFT_STATUS: VoltStatusMap = {
  wartet: { tone: "info", label: "wartet" },
  laeuft: { tone: "info", label: "läuft" },
  fertig: { tone: "success", label: "fertig" },
  fehler: { tone: "destructive", label: "Fehler" },
};

// Cuts an H2 section with its content out of markdown, the last one too:
// JavaScript has no `\Z`, so the end of text is the lookahead `$(?![\s\S])`.
export const removeSection = (text: string, title: string): string => {
  const re = new RegExp(
    `^##\\s*${title}\\s*$[\\s\\S]*?(?=^##\\s|$(?![\\s\\S]))`,
    "m",
  );
  return text.replace(re, "").trim();
};

/** Reason of the traffic light: the "## Ampel" section without its verdict line. */
export const trafficLightReason = (assessment: string | null) =>
  assessment
    ?.match(/^##\s*Ampel\s*$([\s\S]*?)(?=^##\s|$(?![\s\S]))/m)?.[1]
    ?.split("\n")
    .map((z) => z.trim())
    .filter((z) => z && !/^(GRÜN|GELB|ROT)\b/.test(z))
    .join(" ");

export const downloadName = (d: Pick<Draft, "titel" | "thema">) =>
  `${draftTitle(d)
    .slice(0, 60)
    .replace(/[^\wäöüÄÖÜß -]/g, "")}.md`;

/** Non-public Drucksachen show number and link only, never their title. */
export const sourceText = (q: Source) => {
  if (q.art === "drucksache")
    return q.is_public && q.titel !== q.nummer
      ? `${q.nummer} · ${q.titel}`
      : `Drucksache ${q.nummer}`;
  try {
    return q.titel && q.titel !== q.url ? q.titel : new URL(q.url).hostname;
  } catch {
    return q.titel || q.url;
  }
};

export const lightTone = (light: Draft["ampel"]) =>
  light === "GRÜN" ? "success" : light === "ROT" ? "destructive" : "warning";
